import React, { useState, useEffect } from 'react';
import { render } from 'react-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Shared layout components
import Header from './Shared/Header';
import Footer from './Shared/Footer';

// Main pages
import Home from './Pages/Home';
import CollaborativeWorkingAndLearningSpaces from './Pages/CollaborativeWorkingAndLearningSpaces';
import ComputerAndTechnology from './Pages/ComputerAndTechnology';
import PrintingScanningAndPhotocopying from './Pages/PrintingScanningAndPhotocopying';
import LibraryService from './Pages/LibraryService';
import MakerSpace from './Pages/MakerSpace';

import FindResources from './Pages/FindResources';
import FreeOpenAccessResources from './Pages/FreeOpenAccessResources';
import NewspaperMagazine from './Pages/NewspaperMagazine';
import EresourcesDatabases from './Pages/EresourcesDatabases';
import AHSS from './Pages/EresourcesDatabases/AHSS';
import DSSE from './Pages/EresourcesDatabases/DSSE';

import ResearchSupport from './Pages/ResearchSupport';
import BookSession from './Pages/BookSession';
import EventCalendar from './EventCalendar';
import SelfLearningResources from './Pages/SelfLearningResources';

import About from './Pages/About';
import LibraryHours from './Pages/LibraryHours';
import InternshipAndStudentEmploymentOpportunities from './Pages/InternshipAndStudentEmploymentOpportunities';

import BorrowingPolicies from './Pages/BorrowingPolicies';
import ComputerUsePolicy from './Pages/ComputerUsePolicy';
import DiscussionRoomsPolicy from './Pages/DiscussionRoomsPolicy';
import LibrarySpaces from './Pages/LibrarySpaces';
import FinesPenalty from './Pages/FinesPenalty';

import Compliance from './Pages/Compliance';

import AskaLibrarian from './Pages/AskaLibrarian';
import Faq from './Pages/Faq';
import ResourceRequest from './Pages/ResourceRequest';
import RightsandResponsibilities from './Pages/RightsandResponsibilities';
import Eresources from './Pages/SubPages/Eresources';
import LearningSpaces from './Pages/LearningSpaces';
import TodayEvents from './Component/TodayEvents';
import GoogleSearch from './Component/GoogleSearch';
import SearchResults from './Component/SearchResults';
import LibraryServices from './Pages/LibraryServices';

import NotFound from './Pages/NotFound';

// ---------------------------------------------------
// Legacy redirects
// These keep old PascalCase URLs working.
// Example:
// /LibraryHours -> /library-hours
// This helps prevent broken bookmarks and old internal links.
// ---------------------------------------------------
const legacyRedirects = [
  {
    oldPath: '/CollaborativeWorkingAndLearningSpaces',
    newPath: '/collaborative-working-and-learning-spaces',
  },
  {
    oldPath: '/ComputerAndTechnology',
    newPath: '/computer-and-technology',
  },
  {
    oldPath: '/PrintingScanningAndPhotocopying',
    newPath: '/printing-scanning-and-photocopying',
  },
  {
    oldPath: '/LibraryService',
    newPath: '/library-service',
  },
  {
    oldPath: '/MakerSpace',
    newPath: '/maker-space',
  },
  {
    oldPath: '/FindResources',
    newPath: '/find-resources',
  },
  {
    oldPath: '/FreeOpenAccessResources',
    newPath: '/free-open-access-resources',
  },
  {
    oldPath: '/NewspaperMagazine',
    newPath: '/newspaper-magazine',
  },
  {
    oldPath: '/EresourcesDatabases',
    newPath: '/eresources-databases',
  },
  {
    oldPath: '/AHSS',
    newPath: '/ahss',
  },
  {
    oldPath: '/DSSE',
    newPath: '/dsse',
  },
  {
    oldPath: '/ResearchSupport',
    newPath: '/research-support',
  },
  {
    oldPath: '/BookSession',
    newPath: '/book-session',
  },
  {
    oldPath: '/EventCalendar',
    newPath: '/event-calendar',
  },
  {
    oldPath: '/SelfLearningResources',
    newPath: '/self-learning-resources',
  },
  {
    oldPath: '/About',
    newPath: '/about',
  },
  {
    oldPath: '/LibraryHours',
    newPath: '/library-hours',
  },
  {
    oldPath: '/InternshipAndStudentEmploymentOpportunities',
    newPath: '/internship-and-student-employment-opportunities',
  },
  {
    oldPath: '/BorrowingPolicies',
    newPath: '/borrowing-policies',
  },
  {
    oldPath: '/ComputerUsePolicy',
    newPath: '/computer-use-policy',
  },
  {
    oldPath: '/DiscussionRoomsPolicy',
    newPath: '/discussion-rooms-policy',
  },
  {
    oldPath: '/LibrarySpaces',
    newPath: '/library-spaces',
  },
  {
    oldPath: '/FinesPenalty',
    newPath: '/fines-penalty',
  },
  {
    oldPath: '/Compliance',
    newPath: '/compliance',
  },
  {
    oldPath: '/Eresources',
    newPath: '/eresources',
  },
  {
    oldPath: '/LearningSpaces',
    newPath: '/learning-spaces',
  },
  {
    oldPath: '/AskaLibrarian',
    newPath: '/aska-librarian',
  },
  {
    oldPath: '/Faq',
    newPath: '/frequently-asked-questions',
  },
  {
    oldPath: '/ResourceRequest',
    newPath: '/resource-request',
  },
  {
    oldPath: '/RightsandResponsibilities',
    newPath: '/rights-and-responsibilities',
  },
  {
    oldPath: '/GoogleSearch',
    newPath: '/google-search',
  },
  {
    oldPath: '/SearchResults',
    newPath: '/search-results',
  },
  {
    oldPath: '/LibraryServices',
    newPath: '/library-services',
  },
  {
    oldPath: '/TodayEvents',
    newPath: '/today-events',
  },
];

function App() {
  const [loading, setLoading] = useState(false);

  // ---------------------------------------------------
  // Show preloader for 2 seconds on initial load
  // ---------------------------------------------------
  useEffect(() => {
    setLoading(true);

    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    // Cleanup timeout on unmount
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="home-wrapper">
      {loading ? (
        // ---------------------------------------------------
        // Preloader UI
        // ---------------------------------------------------
        <div className="spinner">
          <div className="inner-spinner">
            <img src="/library/assets/img/preloader.gif" alt="Loading" />
          </div>
        </div>
      ) : (
        // ---------------------------------------------------
        // basename="/library" means all public URLs will start with /library
        // Example:
        // path="/about" will be available at /library/about
        // ---------------------------------------------------
        <BrowserRouter basename="/library">
          <Header />

          <Routes>
            {/* ---------------------------------------------------
                Legacy redirects
                These must stay INSIDE <Routes>
                --------------------------------------------------- */}
            {legacyRedirects.map(({ oldPath, newPath }) => (
                <Route
                  key={oldPath}
                  caseSensitive
                  path={oldPath}
                  element={<Navigate to={newPath} replace />}
                />
              ))}

            {/* ---------------------------------------------------
                Main routes using lowercase + dashes
                --------------------------------------------------- */}
            <Route path="/" element={<Home />} />

            <Route
              path="/collaborative-working-and-learning-spaces"
              element={<CollaborativeWorkingAndLearningSpaces />}
            />
            <Route
              path="/computer-and-technology"
              element={<ComputerAndTechnology />}
            />
            <Route
              path="/printing-scanning-and-photocopying"
              element={<PrintingScanningAndPhotocopying />}
            />
            <Route path="/library-service" element={<LibraryService />} />
            <Route path="/maker-space" element={<MakerSpace />} />

            {/* Find Resources */}
            <Route path="/find-resources" element={<FindResources />} />
            <Route
              path="/free-open-access-resources"
              element={<FreeOpenAccessResources />}
            />
            <Route
              path="/newspaper-magazine"
              element={<NewspaperMagazine />}
            />
            <Route
              path="/eresources-databases"
              element={<EresourcesDatabases />}
            />
            <Route path="/ahss" element={<AHSS />} />
            <Route path="/dsse" element={<DSSE />} />

            <Route path="/research-support" element={<ResearchSupport />} />
            <Route path="/book-session" element={<BookSession />} />
            <Route path="/event-calendar" element={<EventCalendar />} />
            <Route
              path="/self-learning-resources"
              element={<SelfLearningResources />}
            />

            <Route path="/about" element={<About />} />
            <Route path="/library-hours" element={<LibraryHours />} />
            <Route
              path="/internship-and-student-employment-opportunities"
              element={<InternshipAndStudentEmploymentOpportunities />}
            />

            <Route
              path="/borrowing-policies"
              element={<BorrowingPolicies />}
            />
            <Route
              path="/computer-use-policy"
              element={<ComputerUsePolicy />}
            />
            <Route
              path="/discussion-rooms-policy"
              element={<DiscussionRoomsPolicy />}
            />
            <Route path="/library-spaces" element={<LibrarySpaces />} />
            <Route path="/fines-penalty" element={<FinesPenalty />} />
            <Route path="/compliance" element={<Compliance />} />

            <Route path="/eresources" element={<Eresources />} />
            <Route path="/learning-spaces" element={<LearningSpaces />} />
            <Route path="/aska-librarian" element={<AskaLibrarian />} />
            <Route path="/frequently-asked-questions" element={<Faq />} />
            <Route path="/resource-request" element={<ResourceRequest />} />
            <Route
              path="/rights-and-responsibilities"
              element={<RightsandResponsibilities />}
            />
            <Route path="/google-search" element={<GoogleSearch />} />
            <Route path="/search-results" element={<SearchResults />} />
            <Route path="/library-services" element={<LibraryServices />} />
            <Route path="/today-events" element={<TodayEvents />} />

              <Route path="*" element={<NotFound />} />
            {/* ---------------------------------------------------
                Optional catch-all 404 route
                Add this later if you create a NotFound component
                Example:
                <Route path="*" element={<NotFound />} />
                --------------------------------------------------- */}
          </Routes>

          <Footer />
        </BrowserRouter>
      )}
    </div>
  );
}

render(<App />, document.getElementById('root'));